import {
  MiddlewareData,
  MiddlewareState,
  Placement,
  arrow,
  autoPlacement,
  autoUpdate,
  computePosition,
  flip,
  getOverflowAncestors,
  hide,
  offset,
  platform,
  shift,
  size
} from '@floating-ui/dom';
import { offsetParent } from 'composed-offset-position';
import { LitElement, ReactiveController, ReactiveControllerHost } from 'lit';
import type { AutoPositionConfig, AutoPositionInterface } from '../util/common-modal';
import { isPopoverSupported } from '../util/popover';

const defaultFallbackPlacements: Placement[] = [
  'top',
  'right',
  'bottom',
  'left',
  'top-start',
  'top-end',
  'bottom-start',
  'bottom-end',
  'right-start',
  'right-end',
  'left-start',
  'left-end'
];
export class AutoPositionController {
  public cleanup: ReturnType<typeof autoUpdate> | undefined;
  private host: ReactiveControllerHost & AutoPositionInterface;
  private isPopoverEnabled = false;
  public placement: string | undefined;
  private autoPositionConfig: AutoPositionConfig | null = null;
  constructor(host: ReactiveControllerHost & AutoPositionInterface) {
    (this.host = host).addController(this as ReactiveController);
  }
  /**
   * Add dynamic position to the floating panel.
   * 1) floating element
   * 2) sync width and height if needed
   * 3) Position of the dropdown panel
   * 4) Hoisting to overflow the container
   * 5) offset gap between the trigger element and floating element
   * 6) trigger element to toggle the panel
   * 7) Overflowing element
   * 8) positions to place the panel
   * 9) ArrowElement reference to show along with the tooltip
   * 10) To enable Flip placement
   * 11) Enabling this can lead to the floating element overlapping the reference element. https://floating-ui.com/docs/shift#crossaxis
   */
  private autoPosition = () => {
    const { floatingElement, position, isInline, referenceElement } = this.autoPositionConfig!;
    if (!floatingElement) {
      return;
    }
    const strategy = !isInline ? 'fixed' : 'absolute';
    floatingElement.style.position = strategy;
    const host = referenceElement || (this.host as ReactiveControllerHost as LitElement);
    this.isPopoverEnabled = !isInline && isPopoverSupported();

    const offsetMiddleware = this.getOffsetMiddleware();
    const placementMiddleware = this.getPlacementMiddleware();
    const hideMiddleware = this.getHideMiddleware();
    const floatingArrow = this.getArrowMiddleware();
    const sizeMiddleware = this.getSizeMiddleware();

    //Push the required middlewares
    const middleware = [offsetMiddleware, ...placementMiddleware, hideMiddleware, floatingArrow!, sizeMiddleware];

    const getOffsetParent =
      strategy === 'absolute' ? (element: Element) => platform.getOffsetParent(element, offsetParent) : platform.getOffsetParent;

    computePosition(host, floatingElement, {
      placement: position === 'auto' ? 'bottom-start' : position,
      middleware: middleware,
      strategy: strategy,
      platform: {
        ...platform,
        getOffsetParent
      }
    }).then(({ x, y, placement, middlewareData }) => {
      const isRtl = platform.isRTL(floatingElement);

      // Browser's viewport (including scrollbar) - visible content display (excluding scrollbar)
      const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth;
      // Determine whether document is inside iFrame. Workaround for https://github.com/floating-ui/floating-ui/issues/3200
      const isInsideIframe = window.frameElement !== null;

      // Negating the width of the scrollbar from the calculated width in case RTL is enabled and document is inside IFrame
      const adjustedX = isRtl && scrollBarWidth > 0 && isInsideIframe ? x - scrollBarWidth : x;

      Object.assign(floatingElement.style, {
        left: `0px`,
        top: `0px`,
        translate: `${adjustedX}px ${y}px`
      });

      if (this.isPopoverEnabled) {
        floatingElement.style.display = middlewareData.hide?.referenceHidden ? 'none' : 'block';
      }
      this.placement = placement;
      this.styleArrowElement(middlewareData, placement);
    });
  };

  // public method called by components using floating ui to open the overlay
  public openOverlay() {
    this.autoPositionConfig = this.getAutoPosition();
    if (!this.autoPositionConfig) return;
    const { floatingElement, referenceElement } = this.autoPositionConfig;
    const autoUpdateOptions = this.host.getAutoUpdateOptions?.() ?? { animationFrame: true };
    this.cleanup?.();
    this.cleanup = autoUpdate(referenceElement!, floatingElement, this.autoPosition, autoUpdateOptions);
    this.toggleFloatingElement(floatingElement, 'block');
    if (this.isPopoverEnabled) {
      floatingElement?.showPopover();
    }
  }

  // public method called by components using floating ui to close the overlay
  public closeOverlay() {
    if (!this.autoPositionConfig) return;
    const { floatingElement } = this.autoPositionConfig;
    this.cleanup?.();
    this.cleanup = undefined;
    this.toggleFloatingElement(floatingElement, 'none');
    if (this.isPopoverEnabled) {
      floatingElement?.hidePopover();
    }
  }

  private getAutoPosition(): AutoPositionConfig {
    return {
      floatingElement: this.host.getFloatingElement(),
      sync: this.host.getSync?.() ?? '',
      position: this.host.getPosition(),
      isInline: this.host.getIsInline(),
      gap: this.host.getGap?.(),
      referenceElement: this.host.getReferenceElement(),
      overflowElement: this.host.getOverflowElement?.(),
      allowedPlacements: this.host.getAllowedPlacements?.(),
      arrowElement: this.host.getArrowElement?.(),
      isFlipAllowed: this.host.getIsFlipAllowed?.(),
      shiftCrossAxis: this.host.getShiftCrossAxis?.()
    };
  }

  private toggleFloatingElement(floatingElement: HTMLElement | null, property: 'none' | 'block') {
    if (!floatingElement) {
      return;
    }
    floatingElement.style.display = property;
  }

  // style the arrow elements as required
  private styleArrowElement(middlewareData: MiddlewareData, placement: Placement) {
    const { arrowElement } = this.autoPositionConfig!;
    if (!arrowElement) {
      return;
    }
    // Resets styles
    if (arrowElement.style) {
      Object.assign(arrowElement.style, {
        top: '',
        bottom: '',
        left: '',
        right: '',
        insetInlineStart: '',
        insetInlineEnd: ''
      });
    }

    const { x: arrowX, y: arrowY } = middlewareData.arrow ?? {}; // get positions of arrow from middleware
    const staticSide: string | undefined = {
      top: 'bottom',
      right: 'left',
      bottom: 'top',
      left: 'right'
    }[placement.split('-')[0]]; // get the opposite of the placement to apply -4px
    let arrowPlacement: string | null | undefined = null;
    let arrowOffset: number | null = null;
    const [mainPlacement, subPlacement] = placement.split('-'); //split the placement positions
    const isVerticalPlacement = ['bottom', 'top'].includes(mainPlacement) && subPlacement;
    const isHorizontalPlacement = ['left', 'right'].includes(mainPlacement) && subPlacement;
    // If the tooltip position has bottom and top, then offset will be done in left and right positions for arrow.
    if (isVerticalPlacement) {
      arrowPlacement = { start: 'inset-inline-start', end: 'inset-inline-end' }[subPlacement];
      arrowOffset = 16;
    } else if (isHorizontalPlacement) {
      arrowPlacement = { start: 'top', end: 'bottom' }[subPlacement];
      arrowOffset = 16;
    }

    Object.assign(arrowElement.style, {
      left: !isVerticalPlacement ? `${arrowX ?? ''}px` : '',
      top: !isHorizontalPlacement ? `${arrowY ?? ''}px` : '',
      ...(arrowPlacement ? { [arrowPlacement]: `${arrowOffset ?? ''}px` } : {}),
      ...(staticSide ? { [staticSide]: '-4px' } : {})
    });
  }

  // get offset middleware
  private getOffsetMiddleware() {
    const { gap = { mainAxis: 0, crossAxis: 0 } } = this.autoPositionConfig!;
    return offset({ mainAxis: gap.mainAxis ?? 0, crossAxis: gap.crossAxis ?? 0 });
  }

  // get position and shift middlewares
  private getPlacementMiddleware() {
    const { position, isFlipAllowed = false, allowedPlacements = defaultFallbackPlacements } = this.autoPositionConfig!;
    const shiftMiddleware = this.getShiftMiddleware();
    if (position === 'auto') {
      return [autoPlacement({ allowedPlacements: allowedPlacements }), shiftMiddleware];
    } else if (isFlipAllowed) {
      return [flip(), shiftMiddleware];
    } else if (this.isPopoverEnabled) {
      return [flip({ fallbackPlacements: allowedPlacements }), shiftMiddleware];
    }
    return [shiftMiddleware];
  }

  // get shift middleware
  private getShiftMiddleware() {
    const { isInline, arrowElement, shiftCrossAxis = false } = this.autoPositionConfig!;
    if (this.isPopoverEnabled) {
      return shift({ boundary: [], crossAxis: shiftCrossAxis });
    } else if (isInline && arrowElement) {
      //used for inline overlays with precise positioning
      return shift({ mainAxis: false });
    }
    return shift();
  }

  // get hide middleware
  private getHideMiddleware() {
    const { referenceElement } = this.autoPositionConfig!;
    const [scrollParent] = getOverflowAncestors(referenceElement as Node);
    if (this.isPopoverEnabled) {
      return hide({
        strategy: 'referenceHidden',
        //workaround: https://github.com/floating-ui/floating-ui/issues/3108
        boundary: scrollParent instanceof Window || scrollParent instanceof VisualViewport ? [] : scrollParent
      });
    }
    return hide({ strategy: 'referenceHidden' });
  }

  // get arrow middleware
  private getArrowMiddleware() {
    const { arrowElement } = this.autoPositionConfig!;
    return arrowElement ? arrow({ element: arrowElement, padding: 16 }) : null;
  }

  // get size middleware
  private getSizeMiddleware() {
    return size({
      apply: (
        state: MiddlewareState & {
          availableWidth: number;
          availableHeight: number;
        }
      ) => {
        const { floatingElement, sync, overflowElement } = this.autoPositionConfig!;

        this.applyAutoPositionStyles(); // remove the styles

        if (this.isPopoverEnabled) {
          const calculatedMaxHeight = Math.max(0, state.availableHeight); // from floating-ui
          const calculatedMaxWidth = Math.max(0, state.availableWidth); // from floating-ui

          // Get max-height if it is already set in the floating element. parseInt('') will give NaN.
          const componentMaxHeight = overflowElement ? parseInt(getComputedStyle(overflowElement).getPropertyValue('max-height'), 10) : NaN;
          const componentMaxWidth = overflowElement ? parseInt(getComputedStyle(overflowElement).getPropertyValue('max-width'), 10) : NaN;

          const maxHeight = this.getFiniteNumber(componentMaxHeight, calculatedMaxHeight);
          const maxWidth = this.getFiniteNumber(componentMaxWidth, calculatedMaxWidth);

          this.applyAutoPositionStyles(maxHeight, maxWidth, 'auto'); //add the styles
        }
        if (!sync) {
          Object.assign(floatingElement.style, {
            width: '',
            height: ''
          });
          return;
        }

        const { width, height } = state.rects.reference;
        Object.assign(floatingElement.style, {
          width: ['width', 'both'].includes(sync) ? `${width}px` : '',
          height: ['height', 'both'].includes(sync) ? `${height}px` : ''
        });
      }
    });
  }

  // set and reset styles
  private applyAutoPositionStyles(maxHeight?: number, maxWidth?: number, overflow?: string): void {
    const { floatingElement } = this.autoPositionConfig!;
    const styles = {
      '--cat-auto-position-max-height': maxHeight ? `${maxHeight}px` : '',
      '--cat-auto-position-max-width': maxWidth ? `${maxWidth}px` : '',
      '--cat-auto-position-overflow': overflow ? 'auto' : ''
    };

    Object.entries(styles).forEach(([property, value]) => {
      if (value) {
        floatingElement.style.setProperty(property, value);
      } else {
        floatingElement.style.removeProperty(property);
      }
    });
  }

  /**
   * 1) If component value is present and it is a valid number and less than calculated value, return componentValue
   * 2) If component value is not a valid number or greater than calculatedValue, return the calculatedValue
   */
  private getFiniteNumber(componentValue: number, calculatedValue: number): number {
    return isFinite(componentValue) ? (componentValue < calculatedValue ? componentValue : calculatedValue) : calculatedValue;
  }
}
